import { useEffect, useState } from 'react'
import { loadGoogleApiScripts, initializeContactsTokenClient, initializeGapiClient } from 'api/services/GoogleApi'

export const useGoogleContacts = () => {
  const [contactsLoaded, setContactsLoaded] = useState(false)
  const [isScriptLoaded, setIsScriptLoaded] = useState(false)
  const [contacts, setContacts] = useState<any[]>([])
  useEffect(() => {
    const loadGoogleScripts = async () => {
      await loadGoogleApiScripts()
      setIsScriptLoaded(true)
    }

    loadGoogleScripts()
  }, [])

  useEffect(() => {
    if (!isScriptLoaded) return

    const loadGoogleContacts = async () => {
      const tokenClient = initializeContactsTokenClient(async (tokenResponse) => {
        if (!tokenResponse?.access_token) {
          setContactsLoaded(true)
          return
        }

        let googleContacts: { name: string; email: string; url: string }[] = []

        if (
          google.accounts.oauth2.hasGrantedAnyScope(tokenResponse, 'https://www.googleapis.com/auth/contacts.readonly')
        ) {
          const connections = await getConnections()
          googleContacts = googleContacts.concat(connections)
        }

        if (
          google.accounts.oauth2.hasGrantedAnyScope(
            tokenResponse,
            'https://www.googleapis.com/auth/contacts.other.readonly',
          )
        ) {
          const otherContacts = await getOtherContacts()
          googleContacts = googleContacts.concat(otherContacts)
        }

        setContacts(googleContacts)
        setContactsLoaded(true)
      })

      await initializeGapiClient()

      // if we want to skip the select account step in the future, we can pass { prompt: '' }
      tokenClient.requestAccessToken({ prompt: 'consent' })

      const sanitizeContacts = (contacts: any) => {
        let sanitizedContacts: { name: string; email: string; url: string }[] = []

        contacts.forEach((contact: any) => {
          let contactObj = {
            name: '',
            email: '',
            url: '',
          }

          if (!contact.emailAddresses) {
            return
          }
          contactObj.email = contact.emailAddresses[0].value

          if (contact.names) {
            contactObj.name = contact.names[0].displayName
          }

          if (contact.photos) {
            contactObj.url = contact.photos[0].url
          }

          sanitizedContacts.push(contactObj)
        })

        return sanitizedContacts
      }

      const getConnections = async () => {
        const connections = await gapi.client.people.people.connections.list({
          resourceName: 'people/me',
          pageSize: 1000,
          personFields: 'names,emailAddresses,photos',
        })

        if (!connections.result?.connections) {
          return []
        }

        return sanitizeContacts(connections.result.connections)
      }

      const getOtherContacts = async () => {
        const otherContacts = await gapi.client.people.otherContacts.list({
          pageSize: 1000,
          readMask: 'names,emailAddresses,photos',
        })

        if (!otherContacts.result?.otherContacts) {
          return []
        }

        return sanitizeContacts(otherContacts.result.otherContacts)
      }
    }

    loadGoogleContacts()
  }, [isScriptLoaded])

  if (!contactsLoaded) return

  return contacts
}
